/*
  Purpose: Assessment Slot Management — Slot List (MUI DataGrid), Create, Edit,
           View, Delete, Assign, Move, Remove Assignment
           + Single-date & Weekly-Recurring (multi-date) slot creation
  Developer: Integrated Feature
  Project: TCMS
*/

import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Row, Col, Form, Modal, Button, Badge, Spinner, Table,
} from "react-bootstrap";
import axios from "axios";
import { useFormik } from "formik";
import * as yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { security_key, BASE_URL } from "../global";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import Moment from "moment";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import LinearProgress from "@mui/material/LinearProgress";
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';

import "dayjs/plugin/isSameOrAfter";
dayjs.extend(isSameOrAfter);

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const fmtDate = (d) => (d ? Moment(d, "YYYY-MM-DD").format("DD-MM-YYYY") : "-");
const fmtTime = (t) => (t ? Moment(t, "HH:mm:ss").format("HH:mm") : "-");
const centKey = () => sessionStorage.getItem("key");

// Client-side preview generator only — the backend independently regenerates
// the date list from (from, to, recurrence) and never trusts a raw array
// sent from the client. This is used ONLY to render the confirmation popup.
const generateWeeklyDates = (fromDate, toDate, maxPreview = 500) => {
  if (!fromDate || !toDate) return [];
  const dates = [];
  let cur = dayjs(fromDate, "YYYY-MM-DD");
  const end = dayjs(toDate, "YYYY-MM-DD");
  while (cur.isSameOrBefore(end, "day") && dates.length < maxPreview) {
    dates.push(cur.format("YYYY-MM-DD"));
    cur = cur.add(7, "day");
  }
  return dates;
};

const CapacityBar = ({ booked, max }) => {
  const pct   = max > 0 ? Math.round((booked / max) * 100) : 0;
  const color = pct >= 100 ? "#dc3545" : pct >= 70 ? "#ffc107" : "#28a745";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
      <div style={{ flex: 1, background: "#f0f0f0", borderRadius: 4, height: 6 }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4, height: 6 }} />
      </div>
      <span style={{ fontSize: 11, color: "#666", whiteSpace: "nowrap" }}>
        {booked}/{max}
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const SlotManagement = () => {

const timezoneIdentifier = sessionStorage.getItem('timezone_identifier') || "Europe/London";

// returns true if the given date+time is in the past relative to "now" in timezoneIdentifier
const isPastDateTime = (dateStr, timeStr) => {
  if (!dateStr) return false;

  const now = Moment.tz(timezoneIdentifier);
  const target = Moment.tz(
    `${dateStr} ${timeStr || '00:00'}`,
    'YYYY-MM-DD HH:mm',
    timezoneIdentifier
  );

  if (!target.isValid()) return false;
  return target.isSameOrBefore(now);
};

// returns true if dateStr is strictly before today (date-only check)
const isPastDate = (dateStr) => {
  if (!dateStr) return false;
  const now = Moment.tz(timezoneIdentifier);
  const target = Moment.tz(dateStr, 'YYYY-MM-DD', timezoneIdentifier);
  return target.isBefore(now.clone().startOf('day'));
};

  // ── list / filter state ───────────────────────────────────────────────────
  const [slots,        setSlots]       = useState([]);
  const [loading,      setLoading]     = useState(true);
  const [filterFrom,   setFilterFrom]  = useState(null);   // date range start
  const [filterTo,     setFilterTo]    = useState(null);   // date range end

  // ── modals ────────────────────────────────────────────────────────────────
  const [modalShowAdd,  setModalShowAdd]  = useState(false);
  const [showDelete,    setDeleteShow]    = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);

  // ── selection / loading ───────────────────────────────────────────────────
  const [selectedSlot,  setSelectedSlot]  = useState(null);
  const [isEditMode,    setIsEditMode]    = useState(false);
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [isDeleting,    setIsDeleting]    = useState(false);

  // ── view modal ────────────────────────────────────────────────────────────
  const [viewData,    setViewData]    = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [removingKey, setRemovingKey] = useState(null);

  // ── move modal ────────────────────────────────────────────────────────────
  const [moveAssign,    setMoveAssign]    = useState(null);
  const [moveTargetKey, setMoveTargetKey] = useState("");
  const [isMoving,      setIsMoving]      = useState(false);

  // ── single vs weekly-recurring (multi-date) creation ──────────────────────
  const [creationMode,        setCreationMode]        = useState("single"); // "single" | "multiple"
  const [showConfirmMultiple, setShowConfirmMultiple]  = useState(false);
  const [pendingMultiDates,   setPendingMultiDates]    = useState([]); // preview only

  const navigate = useNavigate();
  const handleRowLeadViewClick = async (pk_lead_key) => {
    navigate(`/Add-diagnostics`, { state: { fk_lead_key: `${pk_lead_key}` } });
  };

  //generate wedge code
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const encodedCenterKey   = btoa(centKey() || "");
  const encodedSecurityKey = btoa(security_key || "");

  const widgetSnippet = `<!-- Kindly place this where the form should appear -->
    <div id="booking-widget"></div>
    <div id="enquiry-widget"></div>
    <script src="${BASE_URL}widget/widget.js"><\/script>
    <script>
      MyGoorooScheduler.booking({
        container:    "#booking-widget",
        CENTRE_KEY:   "${encodedCenterKey}",
        SECURITY_KEY: "${encodedSecurityKey}"
      });
    <\/script>
    <script>
      MyGoorooScheduler.enquiry({
        container:    "#enquiry-widget",
        CENTRE_KEY:   "${encodedCenterKey}",
        SECURITY_KEY: "${encodedSecurityKey}"
      });
    <\/script>`;

  //preview
  const [activeTab, setActiveTab] = useState("booking");
  const [showWidgetPreview, setShowWidgetPreview] = useState(false);
  const getWidgetPreviewHtml = (centerKey, securityKey) => {
    return `<!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Widget Preview</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css">
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            background: #fff;
          }
          #loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            gap: 12px;
            color: #888;
          }
          .spinner {
            width: 36px;
            height: 36px;
            border: 3px solid #f0f0f0;
            border-top-color: #534AB7;
            border-radius: 50%;
            animation: spin 0.7s linear infinite;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
        </style>
      </head>
      <body>

        <div id="loading">
          <div class="spinner"></div>
          <span style="font-size:13px; color:#888;">Loading widget...</span>
        </div>

        <!-- Widget mount point -->
        <div id="widget-wrapper">
          <div id="booking-widget" class="booking-widget"></div>
        </div>
        <style>
          html, body {
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: auto;
            background: #fff;
          }

          body {
            padding: 20px;
          }
          .widget-modal-overlay{
            width: unset;
            height: unset;
          }
          #booking-widget {
            width: 1450px;
            zoom: 0.6;
            margin: 0 auto;
          }
        </style>

        <!-- Exactly like your working snippet — script first, then init -->
        <script src="${BASE_URL}widget/widget.js"></script>
        <script>
          document.getElementById('loading').style.display = 'none';
          MyGoorooScheduler.booking({
            container:    '#booking-widget',
            CENTRE_KEY:   '${centerKey}',
            SECURITY_KEY: '${securityKey}'
          });
        </script>

      </body>
      </html>`;
  };

  const getWidgetEnquiryPreviewHtml = (centerKey, securityKey) => {
    return `<!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Widget Preview</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css">
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            background: #fff;
          }
          #loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 300px;
            gap: 12px;
            color: #888;
          }
          .spinner {
            width: 36px;
            height: 36px;
            border: 3px solid #f0f0f0;
            border-top-color: #534AB7;
            border-radius: 50%;
            animation: spin 0.7s linear infinite;
          }
          @keyframes spin { to { transform: rotate(360deg); } }
        </style>
      </head>
      <body>

        <div id="loading">
          <div class="spinner"></div>
          <span style="font-size:13px; color:#888;">Loading widget...</span>
        </div>

        <!-- Widget mount point -->
        <div id="widget-wrapper">
          <div id="enquiry-widget" class="enquiry-widget"></div>
        </div>
        <style>
          html, body {
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: auto;
            background: #fff;
          }

          body {
            padding: 20px;
          }
          .widget-modal-overlay{
            width: unset;
            height: unset;
          }
          #enquiry-widget {
            width: 1450px;
            zoom: 0.6;
            margin: 0 auto;
          }
        </style>

        <!-- Exactly like your working snippet — script first, then init -->
        <script src="${BASE_URL}widget/widget.js"></script>
        <script>
          document.getElementById('loading').style.display = 'none';
          MyGoorooScheduler.enquiry({
            container:    '#enquiry-widget',
            CENTRE_KEY:   '${centerKey}',
            SECURITY_KEY: '${securityKey}'
          });
        </script>

      </body>
      </html>`;
  };

  //── fetch ─────────────────────────────────────────────────────────────────

  const fetchSlots = async (fromOverride, toOverride) => {
    setLoading(true);
    try {
      const from = fromOverride !== undefined ? fromOverride : filterFrom;
      const to   = toOverride   !== undefined ? toOverride   : filterTo;
      const payload = {
        security_key,
        mode:       "get_slot_data",
        center_key: centKey(),
        date_from:  from ? dayjs(from).format("YYYY-MM-DD") : undefined,
        date_to:    to   ? dayjs(to).format("YYYY-MM-DD")   : undefined,
      };
      const res = await axios.post(BASE_URL + "API/api_index.php", payload);
      if (res.data?.status === true) setSlots(res.data.data || []);
      else setSlots([]);
    } catch (err) {
      console.error("fetchSlots:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSlots(); }, []); // eslint-disable-line

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    const now = Moment.tz(timezoneIdentifier);
    return dateStr === now.format("YYYY-MM-DD");
  };

  const minTimeForToday = () => {
    const now = Moment.tz(timezoneIdentifier);
    return now.format("HH:mm");
  };

  // ── Formik / Yup ──────────────────────────────────────────────────────────
  // Validation schema depends on creationMode: slot_date_to (the "To Date")
  // is only required when creating multiple (weekly-recurring) slots.
  const slotSchema = useMemo(() => yup.object({
    slot_date: yup.string()
      .required("Date is required.")
      .test("valid", "Invalid date.", (v) => dayjs(v, "YYYY-MM-DD", true).isValid())
      .test("future", "Date cannot be in the past.", function (v) {
        if (!v) return true;
        return !isPastDate(v);
      }),

    slot_date_to: creationMode === "multiple"
      ? yup.string()
          .required("End date is required.")
          .test("valid", "Invalid date.", (v) => dayjs(v, "YYYY-MM-DD", true).isValid())
          .test("after-from", "End date must be on/after start date.", function (v) {
            const { slot_date } = this.parent;
            if (!v || !slot_date) return true;
            return dayjs(v, "YYYY-MM-DD").isSameOrAfter(dayjs(slot_date, "YYYY-MM-DD"));
          })
          .test("range-cap", "Date range is too large (max 2 years).", function (v) {
            const { slot_date } = this.parent;
            if (!v || !slot_date) return true;
            return dayjs(v, "YYYY-MM-DD").diff(dayjs(slot_date, "YYYY-MM-DD"), "day") <= 365;
          })
      : yup.string().nullable(),

    slot_start_time: yup.string()
      .required("Start time is required.")
      .test("not-past", "Start time cannot be in the past.", function (v) {
        const { slot_date } = this.parent;
        if (!v || !slot_date) return true;
        return !isPastDateTime(slot_date, v);
      }),
    slot_end_time: yup.string()
      .required("End time is required.")
      .test("after-start", "End time must be after start time.", function (v) {
        const { slot_start_time } = this.parent;
        if (!v || !slot_start_time) return false;
        return v > slot_start_time;
      }),
    slot_max_member: yup.number()
      .typeError("Must be a number.")
      .required("Capacity is required.")
      .min(1, "Must be at least 1.")
      .max(10, "Maximum capacity allowed is 10 only."),
  }), [creationMode]);

  const formik = useFormik({
    initialValues: {
      slot_date:       null,
      slot_date_to:    null,
      slot_start_time: "",
      slot_end_time:   "",
      slot_max_member: "",
    },
    validationSchema: slotSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const isMultiple = creationMode === "multiple" && !isEditMode;

        const payload = {
          security_key,
          mode:        isEditMode ? "update_slot" : (isMultiple ? "create_slot_multiple" : "create_slot"),
          center_key:  centKey(),
          pk_slot_key: isEditMode ? selectedSlot?.pk_slot_key : undefined,
          slot_start_time: values.slot_start_time,
          slot_end_time:   values.slot_end_time,
          slot_max_member: values.slot_max_member,
        };

        if (isMultiple) {
          // Send the RANGE + recurrence rule — NOT a pre-generated array.
          // The backend independently regenerates the date list and applies
          // its own hard cap, so a tampered/huge client payload can't force
          // an unbounded loop server-side.
          payload.slot_date_from = values.slot_date;
          payload.slot_date_to   = values.slot_date_to;
          payload.recurrence     = "weekly";
        } else {
          payload.slot_date = values.slot_date;
        }

        const res = await axios.post(BASE_URL + "API/api_index.php", payload);
        if (res.data?.status === true) {
          const msg = isMultiple
            ? `${res.data.created_count ?? 0} slot(s) created successfully.` +
              (res.data.skipped_count ? ` ${res.data.skipped_count} skipped (duplicate).` : "")
            : (isEditMode ? "Slot updated successfully." : "Slot created successfully.");
          toast.success(msg, { autoClose: 4000 });
          setModalShowAdd(false);
          formik.resetForm();
          setIsEditMode(false);
          setSelectedSlot(null);
          setCreationMode("single");
          fetchSlots();
        } else {
          toast.error(res.data?.message || "Failed. Please try again.", { autoClose: 700 });
        }
      } catch {
        toast.error("Failed. Please try again.", { autoClose: 700 });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const { values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue, setTouched } = formik;

  // ── Add / Edit ────────────────────────────────────────────────────────────
  const handleAddClick = () => {
    formik.resetForm();
    setIsEditMode(false);
    setSelectedSlot(null);
    setCreationMode("single");
    setModalShowAdd(true);
  };

  const handleEditClick = (slot) => {
    setSelectedSlot({ ...slot });
    formik.setValues({
      slot_date:       slot.slot_date || null,
      slot_date_to:    null,
      slot_start_time: slot.slot_start_time?.slice(0, 5) || "",
      slot_end_time:   slot.slot_end_time?.slice(0, 5)   || "",
      slot_max_member: slot.slot_max_member || "",
    });
    setCreationMode("single"); // editing always targets a single existing slot
    setIsEditMode(true);
    setModalShowAdd(true);
  };

  // ── Custom submit interceptor ─────────────────────────────────────────────
  // Single date  -> submits immediately (no confirmation popup), unchanged behavior.
  // Multiple date -> validates, builds a client-side PREVIEW of the dates, and
  //                  shows a confirmation popup. Only "Yes" triggers formik.handleSubmit()
  //                  (which calls the backend). "No"/dismiss stops the process entirely.
  const handleFormSubmit = (e) => {
    e.preventDefault();
    formik.validateForm().then((formErrors) => {
      formik.setTouched({
        slot_date: true,
        slot_date_to: true,
        slot_start_time: true,
        slot_end_time: true,
        slot_max_member: true,
      });
      if (Object.keys(formErrors).length > 0) return;

      if (creationMode === "multiple" && !isEditMode) {
        const preview = generateWeeklyDates(values.slot_date, values.slot_date_to);
        setPendingMultiDates(preview);
        setShowConfirmMultiple(true); // stop here — wait for user's Yes/No
      } else {
        formik.handleSubmit(); // single date: no popup, submit right away
      }
    });
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteClick = (slot) => { setSelectedSlot({ ...slot }); setDeleteShow(true); };

  const onConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await axios.post(BASE_URL + "API/api_index.php", {
        security_key,
        mode:        "delete_slot",
        center_key:  centKey(),
        pk_slot_key: selectedSlot?.pk_slot_key,
      });
      if (res.data?.status === true) {
        toast.success("Slot deleted successfully.", { autoClose: 700 });
        setDeleteShow(false);
        fetchSlots();
      } else {
        toast.error(res.data?.message || "Failed to delete.", { autoClose: 4000 });
      }
    } catch {
      toast.error("Failed to delete.", { autoClose: 700 });
    } finally {
      setIsDeleting(false);
    }
  };

  // ── View Slot ─────────────────────────────────────────────────────────────
  const handleViewClick = async (slot) => {
    setSelectedSlot({ ...slot });
    setViewData(null);
    setShowViewModal(true);
    setViewLoading(true);
    try {
      const res = await axios.post(BASE_URL + "API/api_index.php", {
        security_key,
        mode:        "view_slot",
        center_key:  centKey(),
        pk_slot_key: slot.pk_slot_key,
      });
      if (res.data?.status === true) {
        setViewData(res.data.data);
      } else {
        toast.error("Failed to load slot details.", { autoClose: 700 });
        setShowViewModal(false);
      }
    } catch {
      toast.error("Failed to load slot details.", { autoClose: 700 });
      setShowViewModal(false);
    } finally {
      setViewLoading(false);
    }
  };

  // ── Remove assignment ─────────────────────────────────────────────────────
  const handleRemoveAssignment = async (assignKey) => {
    setRemovingKey(assignKey);
    try {
      const res = await axios.post(BASE_URL + "API/api_index.php", {
        security_key,
        mode:               "remove_assigned_slot",
        center_key:         centKey(),
        pk_slot_assign_key: assignKey,
      });
      if (res.data?.status === true) {
        toast.success("Assignment removed.", { autoClose: 700 });
        if (selectedSlot) await handleViewClick(selectedSlot);
        fetchSlots();
      } else {
        toast.error(res.data?.message || "Failed to remove.", { autoClose: 700 });
      }
    } catch {
      toast.error("Failed to remove.", { autoClose: 700 });
    } finally {
      setRemovingKey(null);
    }
  };

  // ── Move assignment ───────────────────────────────────────────────────────
  const handleMoveClick = (assignRow) => {
    setMoveAssign(assignRow);
    setMoveTargetKey("");
    setShowMoveModal(true);
  };

  const onConfirmMove = async () => {
    if (!moveTargetKey) { toast.warning("Please select a target slot.", { autoClose: 2500 }); return; }
    setIsMoving(true);
    try {
      const res = await axios.post(BASE_URL + "API/api_index.php", {
        security_key,
        mode:               "move_assigned_slot",
        center_key:         centKey(),
        pk_slot_assign_key: moveAssign?.pk_slot_assign_key,
        new_slot_key:       moveTargetKey,
      });
      if (res.data?.status === true) {
        toast.success("Assignment moved successfully.", { autoClose: 700 });
        setShowMoveModal(false);
        if (selectedSlot) await handleViewClick(selectedSlot);
        fetchSlots();
      } else {
        toast.error(res.data?.message || "Failed to move.", { autoClose: 4000 });
      }
    } catch {
      toast.error("Failed to move.", { autoClose: 700 });
    } finally {
      setIsMoving(false);
    }
  };

  // ── Date range filter handlers ────────────────────────────────────────────
  const applyDateRange = () => fetchSlots();
  const clearDateRange = () => {
    setFilterFrom(null);
    setFilterTo(null);
    fetchSlots(null, null);
  };

  // ── Available badge ───────────────────────────────────────────────────────
  const availBadge = (avail) => {
    if (avail === 0) return <Badge bg="danger" style={{ borderRadius: '4px' }}>Full</Badge>;
    if (avail <= 2)  return <Badge bg="warning" text="dark" style={{ borderRadius: '4px' }}>{avail} left</Badge>;
    return <Badge bg="success" style={{ borderRadius: '4px' }}>{avail} open</Badge>;
  };

  // ── DataGrid columns ──────────────────────────────────────────────────────
  const columns = [
    {
      headerName: "Date",
      field: "slot_date",
      headerClassName: "super-app-theme--header",
      flex: 1,
      valueGetter: (params) => fmtDate(params.row.slot_date),
    },
    {
      headerName: "Time",
      field: "slot_time",
      headerClassName: "super-app-theme--header",
      flex: 1,
      valueGetter: (params) =>
        `${fmtTime(params.row.slot_start_time)} - ${fmtTime(params.row.slot_end_time)}`,
    },
    {
      headerName: "Capacity",
      field: "slot_max_member",
      headerClassName: "super-app-theme--header",
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row.slot_max_member,
    },
    {
      headerName: "Booked",
      field: "booked_count",
      headerClassName: "super-app-theme--header",
      flex: 0.7,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) => params.row.booked_count,
    },
    {
      headerName: "Available",
      field: "available_count",
      headerClassName: "super-app-theme--header",
      flex: 1.2,
      disableExport: false,
      disableColumnFilter: false,
      disableColumnMenu: false,
      filterable: true,
      sortable: false,
      headerAlign: "center",
      valueGetter: (params) => parseInt(params.row.available_count),
      renderCell: (params) => (
        <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 4, justifyContent: "center" }}>
          <CapacityBar booked={params.row.booked_count} max={params.row.slot_max_member} />
          <div style={{ display: "flex", gap: 4 }}>
            <Badge bg="secondary" style={{ fontSize: 10, borderRadius: '4px' }}>{params.row.booked_count} booked</Badge>
            {availBadge(parseInt(params.row.available_count))}
          </div>
        </div>
      ),
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      headerClassName: "super-app-theme--header",
      width: 220,
      disableExport: true,
      disableColumnFilter: true,
      disableColumnMenu: true,
      filterable: false,
      sortable: false,
      headerAlign: "left",
      valueGetter: (params) =>
        `${fmtDate(params.row.slot_date)} ${fmtTime(params.row.slot_start_time)}`,
      renderCell: (params) => {
        const slot = params.row;
        return (
          <div className="d-flex flex-wrap gap-2 align-items-center" style={{ height: "100%" }}>
            <Button variant="outline-secondary" size="sm" style={{ fontSize: 12 }} onClick={() => handleViewClick(slot)}>
              👁 View
            </Button>
            <Button variant="primary" size="sm" style={{ fontSize: 12 }} onClick={() => handleEditClick(slot)}>
              Edit
            </Button>
            <Button variant="danger" size="sm" style={{ fontSize: 12 }} onClick={() => handleDeleteClick(slot)}>
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  // preview of dates shown in the confirmation popup — truncated so a huge
  // range (e.g. daily-for-a-year) doesn't render hundreds of <li> elements
  const previewDates  = pendingMultiDates.slice(0, 52);
  const remainingCount = pendingMultiDates.length - previewDates.length;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <ToastContainer position="top-center" />

      {/* ── Delete Modal ──────────────────────────────────────────────────── */}
      <Modal show={showDelete} centered>
        <Modal.Header><Modal.Title>Delete Slot</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this slot?</p>
          {selectedSlot && (
            <p style={{ fontSize: 13, color: "#666" }}>
              <strong>{fmtDate(selectedSlot.slot_date)}</strong>{" "}
              {fmtTime(selectedSlot.slot_start_time)} - {fmtTime(selectedSlot.slot_end_time)}
            </p>
          )}
          {selectedSlot?.booked_count > 0 && (
            <p className="text-danger" style={{ fontSize: 13 }}>
              ⚠ This slot has {selectedSlot.booked_count} assigned student(s). Remove assignments first.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="pt-0">
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={() => setDeleteShow(false)}>Cancel</Button>
            <Button variant="contained" className="primary-filled" disabled={isDeleting} onClick={onConfirmDelete}>
              {isDeleting ? <><Spinner animation="border" size="sm" /> Deleting...</> : "Yes, Delete"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ── Add / Edit Modal ──────────────────────────────────────────────── */}
      <Modal show={modalShowAdd} onHide={() => { setModalShowAdd(false); setIsEditMode(false); }} size="lg" centered>
        <Form autoComplete="off" onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <h5 className="page-title">{isEditMode ? "Edit Slot" : "Create Slot"}</h5>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              {/* Creation type toggle — only shown when creating (not editing) */}
              {!isEditMode && (
                <Col lg="12" className="mb-3">
                  <h6 className="form-lable">Slot Creation Type</h6>
                  <div className="d-flex gap-4">
                    <Form.Check
                      type="radio"
                      id="mode-single"
                      label="Single Date"
                      checked={creationMode === "single"}
                      onChange={() => { setCreationMode("single"); setFieldValue("slot_date_to", null); }}
                    />
                    <Form.Check
                      type="radio"
                      id="mode-multiple"
                      label="Multiple Dates (Weekly Recurring)"
                      checked={creationMode === "multiple"}
                      onChange={() => setCreationMode("multiple")}
                    />
                  </div>
                </Col>
              )}

              <Col lg="6" md="6" sm="12" className="position-relative mb-3">
                <h6 className="form-lable required-field">
                  {creationMode === "multiple" && !isEditMode ? "From Date" : "Slot Date"}
                </h6>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["year", "month", "day"]}
                    value={values.slot_date ? dayjs(values.slot_date, "YYYY-MM-DD") : null}
                    onChange={(date) =>
                      setFieldValue("slot_date", date ? dayjs(date).format("YYYY-MM-DD") : null)
                    }
                    onBlur={() => setTouched({ ...touched, slot_date: true })}
                    slotProps={{ textField: { readOnly: false, size: "small", id: "slot_date" } }}
                    className={errors.slot_date && touched.slot_date ? "input-error w-100" : "w-100"}
                    minDate={dayjs()}
                    format="DD-MM-YYYY"
                  />
                </LocalizationProvider>
                {errors.slot_date && touched.slot_date && <span className="error">{errors.slot_date}</span>}
              </Col>

              {/* "To Date" — only for multiple-date (weekly recurring) creation */}
              {creationMode === "multiple" && !isEditMode && (
                <Col lg="6" md="6" sm="12" className="position-relative mb-3">
                  <h6 className="form-lable required-field">To Date</h6>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      views={["year", "month", "day"]}
                      value={values.slot_date_to ? dayjs(values.slot_date_to, "YYYY-MM-DD") : null}
                      onChange={(date) =>
                        setFieldValue("slot_date_to", date ? dayjs(date).format("YYYY-MM-DD") : null)
                      }
                      onBlur={() => setTouched({ ...touched, slot_date_to: true })}
                      minDate={values.slot_date ? dayjs(values.slot_date, "YYYY-MM-DD") : dayjs()}
                      slotProps={{ textField: { readOnly: false, size: "small" } }}
                      className={errors.slot_date_to && touched.slot_date_to ? "input-error w-100" : "w-100"}
                      format="DD-MM-YYYY"
                      maxDate={dayjs().add(1, "year")}
                    />
                  </LocalizationProvider>
                  {errors.slot_date_to && touched.slot_date_to && (
                    <span className="error">{errors.slot_date_to}</span>
                  )}
                  <p className="text-muted d-block mt-1" style={{fontSize: "10px"}}>
                    One slot will be created every week on the same weekday as the From Date.
                  </p>
                </Col>
              )}

              <Col lg="6" md="6" sm="12" className="position-relative mb-3">
                <h6 className="form-lable required-field">Maximum Members</h6>
                <Form.Control
                  type="number" min="1"
                  name="slot_max_member" id="slot_max_member"
                  value={values.slot_max_member}
                  onChange={handleChange} onBlur={handleBlur}
                  placeholder="e.g. 10"
                  className={errors.slot_max_member && touched.slot_max_member ? "input-error mb-0" : "mb-0"}
                />
                {errors.slot_max_member && touched.slot_max_member && (
                  <span className="error">{errors.slot_max_member}</span>
                )}
              </Col>

              <Col lg="6" md="6" sm="12" className="position-relative mb-3">
                <h6 className="form-lable required-field">Start Time</h6>
                <Form.Control
                  type="time"
                  name="slot_start_time" id="slot_start_time"
                  value={values.slot_start_time}
                  onChange={handleChange} onBlur={handleBlur}
                  min={isToday(values.slot_date) ? minTimeForToday() : undefined}
                  className={errors.slot_start_time && touched.slot_start_time ? "input-error mb-0" : "mb-0"}
                />
                {errors.slot_start_time && touched.slot_start_time && (
                  <span className="error">{errors.slot_start_time}</span>
                )}
              </Col>

              <Col lg="6" md="6" sm="12" className="position-relative mb-3">
                <h6 className="form-lable required-field">End Time</h6>
                <Form.Control
                  type="time"
                  name="slot_end_time" id="slot_end_time"
                  value={values.slot_end_time}
                  onChange={handleChange} onBlur={handleBlur}
                  className={errors.slot_end_time && touched.slot_end_time ? "input-error mb-0" : "mb-0"}
                />
                {errors.slot_end_time && touched.slot_end_time && (
                  <span className="error">{errors.slot_end_time}</span>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <div className="mx-auto d-flex gap-2">
              <Button variant="contained" className="primary-gray" onClick={() => setModalShowAdd(false)}>Cancel</Button>
              <Button variant="contained" type="submit" className="primary-filled" disabled={isSubmitting}>
                {isSubmitting ? <><Spinner animation="border" size="sm" /> Saving...</> : "Save"}
              </Button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Confirmation Modal — MULTIPLE DATE creation ONLY ───────────────── */}
      {/* Single-date creation never opens this modal; it submits immediately. */}
      <Modal show={showConfirmMultiple} onHide={() => setShowConfirmMultiple(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><h5 className="page-title mb-0" style={{ fontSize: 16 }}>Confirm Weekly Slot Creation</h5></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 13 }}>
            {values.slot_date === values.slot_date_to ? (
              <>
                This will create <strong>{pendingMultiDates.length}</strong> slot on{" "}
                <strong>{fmtDate(values.slot_date)}</strong>, from{" "}
                <strong>{values.slot_start_time}</strong> to{" "}
                <strong>{values.slot_end_time}</strong>, with a capacity of{" "}
                <strong>{values.slot_max_member}</strong>.
              </>
            ) : (
              <>
                This will create <strong>{pendingMultiDates.length}</strong> slot(s), one every week
                from <strong>{fmtDate(values.slot_date)}</strong> to{" "}
                <strong>{fmtDate(values.slot_date_to)}</strong>, each{" "}
                <strong>{values.slot_start_time}</strong> to{" "}
                <strong>{values.slot_end_time}</strong>, with a capacity of{" "}
                <strong>{values.slot_max_member}</strong>.
              </>
            )}
          </p>
          {/* <p style={{ fontSize: 13 }}>
            This will create <strong>{pendingMultiDates.length}</strong> slot(s), one every week
            from <strong>{fmtDate(values.slot_date)}</strong> to <strong>{fmtDate(values.slot_date_to)}</strong>,
            each {values.slot_start_time}-{values.slot_end_time} with capacity {values.slot_max_member}:
          </p> */}
          <div style={{ maxHeight: 180, overflowY: "auto", fontSize: 13, background: "#f8f9fa", borderRadius: 6, padding: "8px 12px" }}>
            <ul className="mb-0 ps-3 slot-dates">
              {previewDates.map((d) => <li key={d}>{fmtDate(d)}</li>)}
              {remainingCount > 0 && <li style={{ color: "#999" }}>...and {remainingCount} more</li>}
            </ul>
          </div>
          <p className="mt-2 mb-0 text-muted" style={{ fontSize: 12 }}>
            Do you want to proceed and create all these slots?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <div className="mx-auto d-flex gap-2">
            <Button
              variant="contained"
              className="primary-gray"
              disabled={isSubmitting}
              onClick={() => setShowConfirmMultiple(false)}
            >
              No, Cancel
            </Button>
            <Button
              variant="contained"
              className="primary-filled"
              disabled={isSubmitting}
              onClick={() => { setShowConfirmMultiple(false); formik.handleSubmit(); }}
            >
              {isSubmitting ? <><Spinner animation="border" size="sm" /> Creating...</> : "Yes, Create All"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ── View Slot Modal ───────────────────────────────────────────────── */}
      <Modal
        show={showViewModal}
        onHide={() => { setShowViewModal(false); setViewData(null); }}
        size="xl" centered scrollable
      >
        <Modal.Header closeButton style={{ borderBottom: "2px solid #f0f0f0" }}>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <span style={{
                width: 32, height: 32, borderRadius: 8, background: "#4A90D915",
                display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16,
              }}>📅</span>
              <div>
                <h5 className="page-title mb-0" style={{ fontSize: 16 }}>Slot Details</h5>
                {viewData?.slot && (
                  <small style={{ color: "#888", fontWeight: 400, fontSize: 12 }}>
                    {fmtDate(viewData.slot.slot_date)} &nbsp;·&nbsp;
                    {fmtTime(viewData.slot.slot_start_time)} - {fmtTime(viewData.slot.slot_end_time)}
                  </small>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "20px 24px" }}>
          {viewLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
              <div className="text-center">
                <Spinner animation="border" variant="primary" style={{ width: 40, height: 40 }} />
                <p className="mt-3" style={{ color: "#888", fontSize: 14 }}>Loading slot details...</p>
              </div>
            </div>
          ) : viewData ? (
            <>
              <Row className="mb-4 g-2">
                {[
                  { label: "Capacity",  value: viewData.slot.slot_max_member },
                  { label: "Booked",    value: viewData.slot.booked_count },
                  { label: "Available", value: viewData.slot.available_count },
                ].map(({ label, value }) => (
                  <Col key={label} xs={6} sm={4} md="auto">
                    <div style={{
                      background: "#f8f9fa", borderRadius: 8,
                      padding: "10px 18px", minWidth: 100, textAlign: "center",
                    }}>
                      <div style={{ fontSize: 11, color: "#888", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px" }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#333", marginTop: 2 }}>{value}</div>
                    </div>
                  </Col>
                ))}
              </Row>

              <h6 style={{ fontWeight: 700, marginBottom: 10, fontSize: 14 }}>
                Assigned Students ({viewData.students.length})
              </h6>
              <div style={{ overflowX: "auto" }}>
                <Table bordered hover size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
                  <thead style={{ background: "#f8f9fa" }}>
                    <tr>
                      <th style={{ width: "5%" }}>#</th>
                      <th style={{ width: "20%" }}>Parents / Guardians Name</th>
                      <th style={{ width: "15%" }}>Student Name</th>
                      <th style={{ width: "20%" }}>Email</th>
                      <th style={{ width: "10%" }}>Phone</th>
                      <th style={{ width: "15%" }}>Help</th>
                      <th style={{ width: "15%", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewData.students.length > 0 ? (
                      viewData.students.map((stu, idx) => (
                        <tr key={stu.pk_slot_assign_key}>
                          <td style={{ verticalAlign: "middle" }}>{idx + 1}</td>
                          <td style={{ verticalAlign: "middle" }}>{stu.perent_name}</td>
                          <td style={{ verticalAlign: "middle" }}>{stu.student_name}</td>
                          <td style={{ verticalAlign: "middle" }}>{stu.par_emailid || "-"}</td>
                          <td style={{ verticalAlign: "middle" }}>{stu.par_phone || "-"}</td>
                          <td style={{ verticalAlign: "middle" }}>
                            {stu.slot_assign_help_content
                              ? JSON.parse(stu.slot_assign_help_content).join(", ")
                              : "-"}
                          </td>
                          <td style={{ verticalAlign: "middle", textAlign: "center" }}>
                            <div className="d-flex justify-content-center gap-1">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                style={{ fontSize: 11 }}
                                onClick={() => handleRowLeadViewClick(stu.pk_lead_key)}
                              >
                                View
                              </Button>
                              <Button
                                size="sm" variant="outline-primary"
                                style={{ fontSize: 11, padding: "2px 10px" }}
                                onClick={() => handleMoveClick(stu)}
                              >
                                Move
                              </Button>
                              <Button
                                size="sm" variant="outline-danger"
                                style={{ fontSize: 11, padding: "2px 10px" }}
                                disabled={removingKey === stu.pk_slot_assign_key}
                                onClick={() => handleRemoveAssignment(stu.pk_slot_assign_key)}
                              >
                                {removingKey === stu.pk_slot_assign_key
                                  ? <Spinner animation="border" size="sm" />
                                  : "Remove"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ textAlign: "center", color: "#aaa", padding: 30 }}>
                          No students assigned to this slot yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </>
          ) : null}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "2px solid #f0f0f0" }}>
          <small style={{ color: "#aaa", fontSize: 11, marginRight: "auto" }}>
            {viewData?.students?.length || 0} student(s) assigned
          </small>
          <Button variant="secondary" size="sm" onClick={() => { setShowViewModal(false); setViewData(null); }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Move Assignment Modal ─────────────────────────────────────────── */}
      <Modal show={showMoveModal} onHide={() => setShowMoveModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="page-title mb-0" style={{ fontSize: 16 }}>Move to Another Slot</h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 13, color: "#555" }}>
            Moving <strong>{moveAssign?.student_name}</strong> from current slot. Select a target slot below.
          </p>
          <Form.Select
            value={moveTargetKey}
            onChange={(e) => setMoveTargetKey(e.target.value)}
            size="sm"
          >
            <option value="">— Select target slot —</option>
              {slots
              .filter((s) =>
                s.pk_slot_key !== selectedSlot?.pk_slot_key &&
                s.available_count > 0 &&
                !isPastDateTime(s.slot_date, s.slot_start_time?.slice(0, 5))
              )
              .map((s) => (
                <option key={s.pk_slot_key} value={s.pk_slot_key}>
                  {fmtDate(s.slot_date)} · {fmtTime(s.slot_start_time)} - {fmtTime(s.slot_end_time)} ({s.available_count} open)
                </option>
              ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={() => setShowMoveModal(false)}>Cancel</Button>
            <Button variant="contained" className="primary-filled" disabled={isMoving || !moveTargetKey} onClick={onConfirmMove}>
              {isMoving ? <><Spinner animation="border" size="sm" /> Moving...</> : "Confirm Move"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={showWidgetModal} onHide={() => setShowWidgetModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title><h5 className="page-title mb-0">Widget Embed Instructions</h5></Modal.Title>
        </Modal.Header>
        <Modal.Body>

          {[
            { n: 1, title: "Open your HTML file",          desc: "Open the HTML page where you want the booking form to appear." },
            { n: 2, title: "Place the container <div>",    desc: "Add <div id=\"booking-widget\"></div> & <div id=\"enquiry-widget\"></div>inside your <body> at the exact location you want the widget to render." },
            { n: 3, title: "Add the widget script",        desc: "Paste both <script> tags right after the container div. The second calls MyGoorooScheduler.booking() & MyGoorooScheduler.enquiry() with keys." },
            { n: 4, title: "Save and reload",              desc: "The slot booking form will render inside #booking-widget & #enquiry-widget automatically on page load." },
          ].map(({ n, title, desc }) => (
            <div key={n} className="d-flex gap-2 align-items-start mb-3">
              <div style={{ width:24, height:24, borderRadius:"50%", background:"#EEEDFE", color:"#3C3489", fontSize:12, fontWeight:500, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>{n}</div>
              <div>
                <p style={{ fontSize:13, fontWeight:500, margin:"0 0 2px" }}>{title}</p>
                <p style={{ fontSize:12, color:"#666", margin:0 }}>{desc}</p>
              </div>
            </div>
          ))}

          <hr />

          <pre style={{ background:"#f8f9fa", border:"1px solid #e0e0e0", borderRadius:8, padding:"14px 16px", fontSize:12, lineHeight:1.7, overflowX:"auto" }}>
            {widgetSnippet}
          </pre>

        </Modal.Body>
        <Modal.Footer>
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={() => setShowWidgetModal(false)}>Close</Button>
            <Button
              variant="contained"
              className="primary-blue"
              onClick={() => setShowWidgetPreview(true)}
            >
              👁 Preview
            </Button>
            <Button variant="contained" className="primary-filled" onClick={() => {
              navigator.clipboard.writeText(widgetSnippet);
              toast.success("Code copied to clipboard!", { autoClose: 2000 });
            }}>Copy Code</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ── Widget Live Preview Modal ─────────────────────────────────────── */}
      <Modal
        show={showWidgetPreview}
        onHide={() => setShowWidgetPreview(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <span style={{ width: 32, height: 32, borderRadius: 8, background: "#EEEDFE", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>👁</span>
              <div>
                <h5 className="page-title mb-0" style={{ fontSize: 16 }}>Widget Live Preview</h5>
                <small style={{ color: "#888", fontWeight: 400, fontSize: 12 }}>Rendering actual widget</small>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <div className="d-flex px-3 pt-3 mb-2" style={{ gap: "10px" }}>
          <button
            className={`btn ${activeTab === "booking" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setActiveTab("booking")}
          >
            Slot Booking
          </button>

          <button
            className={`btn ${activeTab === "contact" ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setActiveTab("contact")}
          >
            Contact Us
          </button>
        </div>

        <Modal.Body style={{ padding: 0 }}>
          <iframe
            key={showWidgetPreview ? "open" : "closed"}
            title="Widget Preview"
            style={{
              width: "100%",
              height: "72vh",
              border: "none",
              display: "block",
              background: "#fff",
            }}
            srcDoc={
              activeTab === "booking"
                ? getWidgetPreviewHtml(encodedCenterKey, encodedSecurityKey)
                : getWidgetEnquiryPreviewHtml(encodedCenterKey, encodedSecurityKey)
            }
          />
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #f0f0f0" }}>
          <div className="mx-auto d-flex gap-2">
            <Button variant="contained" className="primary-gray" onClick={() => setShowWidgetPreview(false)}>Close</Button>
            <Button variant="contained" className="primary-blue" onClick={() => { setShowWidgetPreview(false); setShowWidgetModal(true); }}>← Back to Instructions</Button>
            <Button variant="contained" className="primary-filled" onClick={() => { navigator.clipboard.writeText(widgetSnippet); toast.success("Code copied to clipboard!", { autoClose: 2000 }); }}>Copy Code</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ── Main Page ─────────────────────────────────────────────────────── */}
      <div className="page-wrapper">

        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
                <h5 className='page-title'> Slots </h5>
                <div role="presentation" >
                    <Breadcrumbs aria-label="breadcrumb" className='my-breadcrumbs'>
                        <NavLink underline="hover" color="inherit" to='/calendar'> Home</NavLink>
                        <Typography color="text.primary">Slots</Typography>
                    </Breadcrumbs>
                </div>
          </div>
          <div>
            <Button variant="outline-secondary" onClick={() => setShowWidgetModal(true)} className="me-2">
              &lt;/&gt; Generate Widget Code
            </Button>
            <Button variant="primary" onClick={handleAddClick}>
              + Create Slot
            </Button>
          </div>
        </div>

        <div className="card my-card bg-white">
          <div className="card-body">

            <Row className="mb-3 align-items-end g-2">
              <Col xs={12} sm={6} md={3}>
                <small className="text-muted d-block mb-1">From Date</small>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["year", "month", "day"]}
                    value={filterFrom ? dayjs(filterFrom) : null}
                    onChange={(d) => setFilterFrom(d ? d.toDate() : null)}
                    slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "DD-MM-YYYY" } }}
                    format="DD-MM-YYYY"
                    className="w-100"
                  />
                </LocalizationProvider>
              </Col>

              <Col xs={12} sm={6} md={3}>
                <small className="text-muted d-block mb-1">To Date</small>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    views={["year", "month", "day"]}
                    value={filterTo ? dayjs(filterTo) : null}
                    onChange={(d) => setFilterTo(d ? d.toDate() : null)}
                    minDate={filterFrom ? dayjs(filterFrom) : undefined}
                    slotProps={{ textField: { size: "small", fullWidth: true, placeholder: "DD-MM-YYYY" } }}
                    format="DD-MM-YYYY"
                    className="w-100"
                  />
                </LocalizationProvider>
              </Col>

              <Col xs={12} sm={12} md={6} className="d-flex gap-2">
                <Button variant="primary" size="sm" onClick={applyDateRange}>Search</Button>
                <Button variant="outline-secondary" size="sm" onClick={clearDateRange}>Clear</Button>
              </Col>
            </Row>

            <Box
              sx={{
                height: "fit-content",
                width: "100%",
                "& .actions":                    { color: "text.secondary" },
                "& .textPrimary":                { color: "text.primary" },
                "& .super-app-theme--header":    { backgroundColor: "#e3e6ee" },
                "& .MuiDataGrid-cell:focus":     { outline: "none" },
                "& .MuiDataGrid-row:hover":      { cursor: "default" },
              }}
              style={{ height: "100%", width: "100%" }}
            >
              <DataGrid
                autoHeight
                slots={{
                  loadingOverlay: LinearProgress,
                  toolbar: GridToolbar,
                }}
                slotProps={{
                  toolbar: {
                    csvOptions:   { fileName: "SlotList",  utf8WithBom: true },
                    printOptions: {
                      pageStyle: `@media print { @page { size: A4 landscape; margin: 0; } }`,
                      disableToolbarButton: true,
                      hideFooter: true,
                      hideToolbar: true,
                      fileName: "SlotList",
                    },
                    showQuickFilter: true,
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                loading={loading}
                rows={slots.map((row, index) => ({ ...row, id: `slot-${index}` }))}
                columns={columns}
                rowHeight={60}
                density="compact"
                disableRowSelectionOnClick
                showCellVerticalBorder
                showColumnVerticalBorder
              />
            </Box>

          </div>
        </div>
      </div>
    </>
  );
};

export default SlotManagement;